import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  Building2,
  Users,
  Wallet,
  ArrowLeftRight,
  QrCode,
  FileText,
  Settings,
  Shield,
  ChevronLeft,
  ChevronDown,
  Zap,
  Key,
  ClipboardList,
  Landmark,
  Calculator,
  Blocks,
  Server,
  Activity,
  Cog,
  Bitcoin,
  Layers,
  Coins,
  Home,
  Send,
  BarChart3,
  Crown,
  Terminal,
  Gift,
  Heart,
  FileSignature,
  Briefcase,
  TrendingUp,
  PieChart,
  ShieldCheck,
  Scan,
  ScanFace,
  Palette,
  Boxes,
  Sparkles,
  FileCode,
  Bell,
  GripVertical,
  Calendar,
  RotateCcw,
  Link2,
  Receipt,
  ListChecks,
  RefreshCw,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useThemeStore, SIDEBAR_MIN_WIDTH, SIDEBAR_MAX_WIDTH, SIDEBAR_COLLAPSED_WIDTH } from '../../stores/theme.store';
import { useAuthStore, ProviderCategory } from '../../stores/auth.store';
import { SetupProgress } from '../admin/SetupWizard';
import { AlertBadge } from '../financial-security';
import { useTenantModules } from '../../hooks/useModules';
import { useTenantFeatures } from '../../hooks/useTenantFeatures';
import { PlatformModule } from '../../types/modules';

interface NavItem {
  name: string;
  href?: string;
  icon: React.ElementType;
  adminOnly?: boolean;
  superAdminOnly?: boolean;
  category?: ProviderCategory; // Only show if tenant has this category enabled
  module?: PlatformModule; // Only show if this module is enabled for tenant
  feature?: { module: string; key: string }; // Granular feature toggle (e.g., pixEnabled)
  children?: NavItem[];
  showAlertBadge?: boolean; // Show financial security alert badge
  dataTour?: string; // data-tour attribute for onboarding tours
}

// Super Admin Navigation
const superAdminNavigation: NavItem[] = [
  { name: 'Dashboard Global', href: '/super-admin', icon: LayoutDashboard },
  { name: 'Tenants', href: '/super-admin/tenants', icon: Building2 },
  { name: 'Usuários Globais', href: '/super-admin/users', icon: Users },
  { 
    name: 'Temas',
    icon: Palette,
    children: [
      { name: 'Editor Básico', href: '/super-admin/theming', icon: Palette },
      { name: 'Editor Avançado', href: '/super-admin/theming-advanced', icon: Sparkles },
      { name: 'Temas Imersivos', href: '/admin/immersive-themes', icon: Layers },
      { name: 'Layouts Futurísticos', href: '/admin/futuristic-layouts', icon: Sparkles },
    ],
  },
  {
    name: 'Providers',
    icon: Blocks,
    children: [
      { name: 'Instâncias', href: '/super-admin/providers', icon: Server },
      { name: 'Templates', href: '/super-admin/provider-templates', icon: Blocks },
    ],
  },
  {
    name: 'Taxas da Plataforma',
    icon: Calculator,
    children: [
      { name: 'Configuração', href: '/super-admin/platform-fees', icon: Calculator },
      { name: 'Custos Provider', href: '/super-admin/provider-costs', icon: Server },
      { name: 'Health Check', href: '/super-admin/fee-health', icon: Activity },
    ],
  },
  {
    name: 'Receita',
    icon: BarChart3,
    children: [
      { name: 'Dashboard', href: '/super-admin/revenue', icon: BarChart3 },
      { name: 'Por Tenant', href: '/super-admin/revenue/tenants', icon: Building2 },
    ],
  },
  {
    name: 'Financeiro',
    icon: Wallet,
    children: [
      { name: 'Balancete Geral', href: '/admin/financial-summary', icon: PieChart },
      { name: 'AnyPayBR', href: '/admin/anypay', icon: Wallet },
      { name: 'PIX Nominal (Empresa)', href: '/admin/anypay/company-register', icon: Building2 },
      { name: 'Conciliação', href: '/admin/reconciliation', icon: RefreshCw },
    ],
  },
  {
    name: 'Segurança',
    icon: Shield,
    children: [
      { name: 'Verificação Facial', href: '/super-admin/facial-verification', icon: Scan },
      { name: 'Segurança Financeira', href: '/super-admin/financial-security', icon: Shield },
      { name: 'Saúde Financeira', href: '/super-admin/financial-health', icon: Activity, showAlertBadge: true },
    ],
  },
  {
    name: 'Tokenização',
    icon: Briefcase,
    children: [
      { name: 'Dashboard Global', href: '/super-admin/tokenization', icon: LayoutDashboard },
      { name: 'Tipos de Ativos', href: '/super-admin/asset-types', icon: Briefcase },
      { name: 'Categorias por Tenant', href: '/super-admin/tenant-categories', icon: Building2 },
      { name: 'Gestão de Tokens', href: '/super-admin/tokens', icon: Coins },
      { name: 'Regras de Compliance', href: '/super-admin/compliance-rules', icon: ShieldCheck },
      { name: 'Configuração Oracle', href: '/super-admin/oracle', icon: Activity },
    ],
  },
  { name: 'Empresas PJ', href: '/super-admin/companies', icon: Building2 },
  { name: 'Módulos da Plataforma', href: '/super-admin/modules', icon: Boxes },
  { name: 'Monitoramento Global', href: '/super-admin/monitoring', icon: Activity },
  { name: 'Volume de Transações', href: '/super-admin/transactions', icon: BarChart3 },
  { name: 'Meu Perfil', href: '/profile', icon: Users },
  { name: 'Configurações', href: '/settings', icon: Settings },
];

// Tenant Admin Navigation
const tenantAdminNavigation: NavItem[] = [
  { name: 'Dashboard', href: '/admin', icon: LayoutDashboard, dataTour: 'dashboard' },
  { name: 'Setup Inicial', href: '/admin/setup', icon: Cog, adminOnly: true },
  { name: 'Resumo Financeiro', href: '/admin/financial-summary', icon: PieChart, adminOnly: true },
  { 
    name: 'Relatórios Detalhados',
    icon: BarChart3,
    adminOnly: true,
    children: [
      { name: 'Hub de Relatórios', href: '/admin/reports/hub', icon: BarChart3 },
      { name: 'Consolidado', href: '/admin/reports/consolidated', icon: PieChart },
      { name: 'Por Correntista', href: '/admin/reports/per-customer', icon: Users },
      { name: 'Taxas e Mensalidades', href: '/admin/reports/fees-membership', icon: Receipt },
      { name: 'Transações Detalhadas', href: '/admin/reports/detailed-transactions', icon: ListChecks },
    ],
  },
  { 
    name: 'Contas',
    icon: Wallet,
    module: PlatformModule.CORE_BANKING,
    children: [
      { name: 'Contas Bancárias', href: '/accounts', icon: Wallet },
      { name: 'Contas Virtuais', href: '/virtual-accounts', icon: Coins },
    ],
  },
  { name: 'Transações', href: '/transactions', icon: ArrowLeftRight, module: PlatformModule.CORE_BANKING },
  {
    name: 'Tokenização (RWA)',
    icon: Briefcase,
    adminOnly: true,
    showAlertBadge: true,
    module: PlatformModule.TOKENIZATION,
    children: [
      { name: 'Dashboard', href: '/admin/tokenization', icon: LayoutDashboard },
      { name: 'Meus Ativos', href: '/admin/tokenization/assets', icon: Briefcase },
      { name: 'Novo Ativo', href: '/admin/tokenization/assets/new', icon: Briefcase },
      { name: 'Gestão de Supply', href: '/admin/tokenization/supply', icon: Coins },
      { name: 'Config. Token', href: '/admin/tokenization/token-config', icon: Cog },
      { name: 'Config. Yield', href: '/admin/tokenization/yield', icon: TrendingUp },
      { name: 'Compliance', href: '/admin/tokenization/compliance', icon: ShieldCheck },
      { name: 'Saúde Financeira', href: '/admin/tokenization/financial-health', icon: Activity, showAlertBadge: true },
      { name: 'Config. Segurança', href: '/admin/tokenization/security-settings', icon: Shield },
      { name: 'Log de Auditoria', href: '/admin/tokenization/security-audit', icon: FileText },
      { 
        name: 'Smart Contracts',
        icon: FileCode,
        children: [
          { name: 'Templates', href: '/admin/tokenization/contracts', icon: FileCode },
          { name: 'Auditorias', href: '/admin/tokenization/contracts/audits', icon: ShieldCheck },
        ],
      },
    ],
  },
  {
    name: 'Contratos Digitais',
    icon: FileSignature,
    adminOnly: true,
    module: PlatformModule.CORE_BANKING,
    feature: { module: 'CORE_BANKING', key: 'digitalContractsEnabled' },
    children: [
      { name: 'Dashboard', href: '/admin/contracts', icon: LayoutDashboard },
      { name: 'Lista de Contratos', href: '/admin/contracts/list', icon: FileText },
      { name: 'Novo Contrato', href: '/admin/contracts/new', icon: FileSignature },
      { name: 'Templates', href: '/admin/contracts/templates', icon: FileText },
    ],
  },
  {
    name: 'Benefícios',
    icon: Gift,
    adminOnly: true,
    module: PlatformModule.BENEFITS,
    children: [
      { name: 'Planos', href: '/admin/benefits/plans', icon: Gift },
      { name: 'Adesões', href: '/admin/benefits/subscriptions', icon: Heart },
      { name: 'Dashboard', href: '/admin/benefits/dashboard', icon: BarChart3 },
    ],
  },
  {
    name: 'Compliance',
    icon: ShieldCheck,
    adminOnly: true,
    dataTour: 'compliance-queue',
    children: [
      { name: 'MED (Devolução PIX)', href: '/admin/compliance/med', icon: ArrowLeftRight },
      { 
        name: 'PLD/FT',
        icon: Shield,
        children: [
          { name: 'Dashboard', href: '/admin/compliance/pld', icon: LayoutDashboard },
          { name: 'Alertas', href: '/admin/compliance/pld/alerts', icon: Bell },
          { name: 'Regras', href: '/admin/compliance/pld/rules', icon: FileText },
          { name: 'Configurações', href: '/admin/compliance/pld/config', icon: Settings },
        ],
      },
      { 
        name: 'COAF',
        icon: FileText,
        children: [
          { name: 'Dashboard', href: '/admin/compliance/coaf', icon: LayoutDashboard },
          { name: 'Comunicações', href: '/admin/compliance/coaf/list', icon: FileText },
          { name: 'Nova Comunicação', href: '/admin/compliance/coaf/new', icon: FileSignature },
        ],
      },
      { name: 'Configurações Gerais', href: '/admin/compliance/config', icon: Settings },
    ],
  },
  {
    name: 'Administração',
    icon: Shield,
    adminOnly: true,
    children: [
      // Sub-grupo: Usuários e Acessos
      {
        name: 'Usuários e Acessos',
        icon: Users,
        children: [
          { name: 'Usuários', href: '/admin/users', icon: Users, dataTour: 'users' },
          { name: 'Entidades', href: '/admin/entities', icon: Building2 },
          { name: 'Permissões', href: '/admin/permissions', icon: Key },
          { name: 'Parceiros', href: '/admin/partners', icon: Building2 },
          { name: 'Gerentes', href: '/admin/managers', icon: Users },
          { name: 'Grupos Econômicos', href: '/admin/economic-groups', icon: Building2 },
          { name: 'Links de Convite', href: '/admin/invite-links', icon: Link2 },
        ],
      },
      // Sub-grupo: Financeiro (consolidado)
      {
        name: 'Financeiro',
        icon: Calculator,
        children: [
          { name: 'Resumo Financeiro', href: '/admin/financial-summary', icon: BarChart3 },
          { name: 'Visão Financeira', href: '/admin/financial-overview', icon: BarChart3, superAdminOnly: true },
          { name: 'Conciliação', href: '/admin/reconciliation', icon: RefreshCw },
          { name: 'Contestações', href: '/admin/disputes', icon: FileText },
          { name: 'AnyPayBR', href: '/admin/anypay', icon: Wallet, superAdminOnly: true },
          { name: 'Taxas de Adesão', href: '/admin/membership-fees', icon: Calculator },
        ],
      },
      // Sub-grupo: Segurança
      {
        name: 'Segurança',
        icon: Shield,
        children: [
          { name: 'Segurança', href: '/admin/security-config', icon: Shield, adminOnly: true },
          { name: 'Verificação Facial', href: '/admin/facial-verification', icon: Scan, module: PlatformModule.ADVANCED_SECURITY },
          { name: 'Config. Facial Avançada', href: '/admin/facial-config', icon: ScanFace, module: PlatformModule.ADVANCED_SECURITY },
          { 
            name: 'Segurança Avançada', 
            icon: Shield,
            module: PlatformModule.ADVANCED_SECURITY,
            children: [
              { name: 'Configurações', href: '/admin/security/config', icon: Settings },
              { name: 'Logs de Auditoria', href: '/admin/security/audit', icon: ClipboardList },
            ],
          },
          { name: 'Logs de Auditoria', href: '/admin/audit', icon: ClipboardList, dataTour: 'audit-logs' },
        ],
      },
      // Sub-grupo: Operações Bancárias
      {
        name: 'Operações Bancárias',
        icon: Landmark,
        module: PlatformModule.CORE_BANKING,
        children: [
          { name: 'Conexões Bancárias', href: '/admin/bank-connections', icon: Landmark },
          { name: 'Empresas PJ', href: '/admin/companies', icon: Building2 },
          { 
            name: 'Análise de KYC', 
            icon: ClipboardList,
            children: [
              { name: 'Dashboard', href: '/admin/kyc/dashboard', icon: LayoutDashboard },
              { name: 'Fila de Análise', href: '/admin/kyc', icon: ClipboardList },
              { name: 'Configurações', href: '/admin/kyc/settings', icon: Settings },
              { name: 'Analistas', href: '/admin/kyc/analysts', icon: Users },
              { name: 'Templates', href: '/admin/kyc/templates', icon: FileText },
              { name: 'Relatórios', href: '/admin/kyc/reports', icon: BarChart3 },
            ],
          },
        ],
      },
      // Sub-grupo: Módulos Premium
      {
        name: 'Módulos Premium',
        icon: Sparkles,
        children: [
          {
            name: 'Motor de Crédito',
            icon: Coins,
            module: PlatformModule.CREDIT_ENGINE,
            children: [
              { name: 'Dashboard', href: '/admin/credit/dashboard', icon: LayoutDashboard },
              { name: 'Políticas', href: '/admin/credit/policies', icon: FileText },
              { name: 'Produtos', href: '/admin/credit/products', icon: Briefcase },
              { name: 'Solicitações', href: '/admin/credit/requests', icon: ClipboardList },
              { name: 'Empréstimos', href: '/admin/credit/loans', icon: Coins },
              { name: 'Cobrança', href: '/admin/credit/collection', icon: Calculator },
              { name: 'Relatórios', href: '/admin/credit/reports', icon: BarChart3 },
            ],
          },
          {
            name: 'Open Finance',
            icon: Landmark,
            module: PlatformModule.OPEN_FINANCE,
            children: [
              { name: 'Configuração', href: '/admin/open-finance/config', icon: Settings },
              { name: 'Monitor', href: '/admin/open-finance/monitor', icon: Activity },
            ],
          },
          {
            name: 'Cartões',
            icon: Wallet,
            module: PlatformModule.CARD_ISSUING,
            children: [
              { name: 'Configuração', href: '/admin/cards/config', icon: Settings },
              { name: 'Gestão', href: '/admin/cards/management', icon: ClipboardList },
            ],
          },
          {
            name: 'Investimentos',
            icon: TrendingUp,
            module: PlatformModule.INVESTMENTS,
            children: [
              { name: 'Produtos', href: '/admin/investments/products', icon: Briefcase },
              { name: 'Configuração', href: '/admin/investments/config', icon: Settings },
            ],
          },
        ],
      },
      // Sub-grupo: Sistema
      {
        name: 'Sistema',
        icon: Cog,
        children: [
          { name: 'Módulos', href: '/admin/modules', icon: Boxes, dataTour: 'modules' },
          { name: 'Temas Imersivos', href: '/admin/immersive-themes', icon: Layers },
          { name: 'Layouts Futurísticos', href: '/admin/futuristic-layouts', icon: Sparkles },
          { name: 'Saúde do Sistema', href: '/admin/system-health', icon: Activity },
        ],
      },
    ],
  },
  {
    name: 'Provider Engine',
    icon: Cog,
    adminOnly: true,
    feature: { module: 'CORE_BANKING', key: 'providerEngineVisible' },
    dataTour: 'providers',
    children: [
      { name: 'Templates', href: '/admin/provider-templates', icon: Blocks },
      { name: 'Marketplace', href: '/admin/marketplace', icon: Briefcase },
      { name: 'Instâncias', href: '/admin/provider-instances', icon: Server },
      { name: 'Recursos', href: '/admin/resources', icon: Cog },
      { name: 'Config. Taxas', href: '/admin/fee-config', icon: Calculator },
      { name: 'Test Console', href: '/admin/test-console', icon: Terminal },
      { name: 'Monitoramento', href: '/admin/monitoring', icon: Activity },
    ],
  },
  {
    name: 'Gerente',
    icon: Users,
    adminOnly: true,
    children: [
      { name: 'Dashboard', href: '/manager', icon: LayoutDashboard },
      { name: 'Aprovações', href: '/manager/approvals', icon: ClipboardList },
      { name: 'Comissões', href: '/manager/commissions', icon: Calculator },
      { name: 'Usuários', href: '/manager/users', icon: Users },
    ],
  },
  {
    name: 'Minha Conta',
    icon: Wallet,
    children: [
      { name: 'Meu Perfil', href: '/profile', icon: Users },
      { name: 'Limites', href: '/user/limits', icon: BarChart3 },
      { name: 'Minhas Aprovações', href: '/user/approvals', icon: ClipboardList },
    ],
  },
  { name: 'Configurações', href: '/settings', icon: Settings },
];

// Internet Banking Navigation is now dynamically generated by getBankingNavigationForProfile()
// based on user type (CUSTOMER, SUPPLIER, EMPLOYEE)

export function Sidebar() {
  const location = useLocation();
  const { sidebarCollapsed, sidebarWidth, toggleSidebar, setSidebarWidth } = useThemeStore();
  const { user, isSuperAdmin, isCorrentista, hasCategory } = useAuthStore();
  const [expandedMenus, setExpandedMenus] = useState<string[]>([
    // Admin groups
    'Administração', 'Usuários e Acessos', 'Financeiro', 'Segurança', 'Operações Bancárias', 
    'Módulos Premium', 'Sistema', 'Análise de KYC', 'Segurança Avançada', 'Motor de Crédito', 
    'Open Finance', 'Cartões', 'Investimentos', 'Relatórios Detalhados',
    // Other groups
    'Provider Engine', 'Contas', 'Providers', 'Taxas da Plataforma', 'Receita', 'Benefícios', 
    'Criptomoedas', 'Tokenização', 'Contratos Digitais', 'Tokens (RWA)', 'Contratos', 
    'Verificação KYC', 'Crédito', 'Meus Cartões', 'Blockchain', 'DeFi', 'Tokenização (RWA)', 
    'Smart Contracts',
  ]);

  // Resize state
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);

  // Handle resize mouse events
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizing) return;
    const newWidth = e.clientX;
    setSidebarWidth(newWidth);
  }, [isResizing, setSidebarWidth]);

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  // Add/remove global mouse listeners for resize
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  // Calculate actual width based on collapsed state
  const actualWidth = sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : sidebarWidth;

  // Fetch tenant modules to filter navigation
  const { data: tenantModules } = useTenantModules(user?.tenantId);
  
  // Fetch granular feature flags for each module
  const { isFeatureEnabled } = useTenantFeatures();
  
  // Create a set of enabled modules for quick lookup
  const enabledModules = useMemo(() => {
    if (!tenantModules?.modules) return new Set<PlatformModule>();
    return new Set(
      tenantModules.modules
        .filter(m => m.isEnabled)
        .map(m => m.id as PlatformModule)
    );
  }, [tenantModules]);

  // Check if a module is enabled
  const isModuleEnabled = (moduleId: PlatformModule): boolean => {
    // Super Admin sees everything
    if (isSuperAdmin()) return true;
    return enabledModules.has(moduleId);
  };

  const toggleMenu = (name: string) => {
    setExpandedMenus(prev =>
      prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]
    );
  };

  // Select navigation based on user role
  const getNavigation = (): NavItem[] => {
    if (isSuperAdmin()) {
      return superAdminNavigation;
    }
    if (isCorrentista()) {
      // Return profile-specific navigation based on user type
      return getBankingNavigationForProfile(user?.type || 'CUSTOMER');
    }
    return tenantAdminNavigation;
  };

  // Get banking navigation with profile-specific routes
  const getBankingNavigationForProfile = (userType: string): NavItem[] => {
    const profilePath = userType === 'SUPPLIER' ? '/banking/supplier' 
                      : userType === 'EMPLOYEE' ? '/banking/employee' 
                      : '/banking/customer';
    
    // Base navigation items that vary by profile
    const baseItems: NavItem[] = [
      { name: 'Início', href: profilePath, icon: Home, dataTour: 'dashboard' },
    ];

    // Customer-specific items (require CORE_BANKING)
    if (userType === 'CUSTOMER') {
      baseItems.push(
        { name: 'Minha Empresa', href: '/banking/company/status', icon: Building2 },
        { name: 'Pix', href: '/banking/customer/pix', icon: QrCode, module: PlatformModule.CORE_BANKING, feature: { module: 'CORE_BANKING', key: 'pixEnabled' }, dataTour: 'pix' },
        { name: 'PIX Agendado', href: '/banking/pix/scheduled', icon: Calendar, module: PlatformModule.CORE_BANKING, feature: { module: 'CORE_BANKING', key: 'pixScheduledEnabled' } },
        { name: 'Pagamentos', href: '/banking/customer/payments', icon: Wallet, module: PlatformModule.CORE_BANKING },
        { name: 'Transferir', href: '/banking/customer/transfers', icon: Send, module: PlatformModule.CORE_BANKING, feature: { module: 'CORE_BANKING', key: 'transferEnabled' }, dataTour: 'transfer' },
        { name: 'Extrato', href: '/banking/customer/statement', icon: FileText, module: PlatformModule.CORE_BANKING, feature: { module: 'CORE_BANKING', key: 'statementEnabled' }, dataTour: 'statement' },
        { name: 'Pagamento de Contas', href: '/banking/bills', icon: FileText, module: PlatformModule.CORE_BANKING, feature: { module: 'CORE_BANKING', key: 'boletoEnabled' } },
        { name: 'Contestar Pagamento', href: '/banking/disputes', icon: FileText, module: PlatformModule.CORE_BANKING },
      );
    }

    // Supplier-specific items (require CORE_BANKING)
    if (userType === 'SUPPLIER') {
      baseItems.push(
        { name: 'Cobranças', href: '/banking/supplier/charges', icon: FileText, module: PlatformModule.CORE_BANKING },
        { name: 'Clientes', href: '/banking/supplier/customers', icon: Users, module: PlatformModule.CORE_BANKING },
        { name: 'Saques', href: '/banking/supplier/withdrawals', icon: Wallet, module: PlatformModule.CORE_BANKING },
        { name: 'Relatórios', href: '/banking/supplier/reports', icon: BarChart3, module: PlatformModule.CORE_BANKING },
      );
    }

    // Employee-specific items
    if (userType === 'EMPLOYEE') {
      baseItems.push(
        { name: 'Pix', href: '/banking/customer/pix', icon: QrCode, module: PlatformModule.CORE_BANKING, feature: { module: 'CORE_BANKING', key: 'pixEnabled' } },
        { name: 'Transferir', href: '/banking/customer/transfers', icon: Send, module: PlatformModule.CORE_BANKING, feature: { module: 'CORE_BANKING', key: 'transferEnabled' } },
        { name: 'Adiantamento', href: '/banking/employee/salary-advance', icon: Coins },
        { name: 'Benefícios', href: '/banking/employee/benefits', icon: Gift, module: PlatformModule.BENEFITS },
        { name: 'Holerite', href: '/banking/employee/payslip', icon: FileText },
        { name: 'Solicitações RH', href: '/banking/employee/hr-requests', icon: ClipboardList },
      );
    }

    // Common items for all profiles - filtered by module
    const commonItems: NavItem[] = [
      {
        name: 'Blockchain',
        icon: Blocks,
        module: PlatformModule.CRYPTO,
        children: [
          { 
            name: 'Criptomoedas',
            icon: Bitcoin,
            children: [
              { name: 'Dashboard', href: '/banking/crypto', icon: Bitcoin },
              { name: 'Carteiras', href: '/banking/crypto/wallets', icon: Wallet },
              { name: 'Marketplace P2P', href: '/banking/crypto/marketplace', icon: Building2 },
              { name: 'Histórico', href: '/banking/crypto/history', icon: FileText },
            ],
          },
          { 
            name: 'DeFi',
            icon: Layers,
            children: [
              { name: 'Pools', href: '/banking/defi/pools', icon: Layers },
              { name: 'Minhas Posições', href: '/banking/defi/positions', icon: BarChart3 },
              { name: 'Staking', href: '/banking/staking', icon: Coins },
              { name: 'Yield Farming', href: '/banking/farming', icon: TrendingUp },
              { name: 'Bridge', href: '/banking/bridge', icon: ArrowLeftRight },
              { name: 'Swap Avançado', href: '/banking/swap', icon: ArrowLeftRight },
              { name: 'NFT Gallery', href: '/banking/nft', icon: Sparkles },
            ],
          },
          {
            name: 'Tokens (RWA)',
            icon: Briefcase,
            module: PlatformModule.TOKENIZATION,
            children: [
              { name: 'Marketplace', href: '/banking/tokens', icon: Briefcase },
              { name: 'Meu Portfólio', href: '/banking/portfolio', icon: PieChart },
              { name: 'Minhas Ordens', href: '/banking/orders', icon: ClipboardList },
              { name: 'Meus Rendimentos', href: '/banking/yields', icon: TrendingUp },
            ],
          },
        ],
      },
      {
        name: 'Crédito',
        icon: Coins,
        module: PlatformModule.CREDIT_ENGINE,
        children: [
          { name: 'Simular Empréstimo', href: '/banking/credit/simulate', icon: Calculator },
          { name: 'Solicitar Crédito', href: '/banking/credit/request', icon: FileSignature },
          { name: 'Meus Empréstimos', href: '/banking/credit/loans', icon: Coins },
        ],
      },
      { name: 'Consentimentos', href: '/banking/consents', icon: ShieldCheck, module: PlatformModule.OPEN_FINANCE },
      {
        name: 'Meus Cartões',
        icon: Wallet,
        module: PlatformModule.CARD_ISSUING,
        children: [
          { name: 'Cartões', href: '/banking/cards', icon: Wallet },
          { name: 'Solicitar Cartão', href: '/banking/cards/request', icon: Wallet },
        ],
      },
      {
        name: 'Investimentos',
        icon: TrendingUp,
        module: PlatformModule.INVESTMENTS,
        children: [
          { name: 'Marketplace', href: '/banking/investments', icon: Briefcase },
          { name: 'Meu Portfólio', href: '/banking/investments/portfolio', icon: PieChart },
          { name: 'Perfil Investidor', href: '/banking/investments/profile', icon: Users },
          { name: 'Simulador', href: '/banking/investments/simulate', icon: Calculator },
        ],
      },
      {
        name: 'Verificação KYC',
        icon: Scan,
        module: PlatformModule.CORE_BANKING,
        children: [
          { name: 'Submeter KYC', href: '/banking/kyc', icon: Scan },
          { name: 'Status', href: '/banking/kyc/status', icon: ShieldCheck },
          { name: 'Verificação de Identidade', href: '/banking/kyc/verify', icon: Shield },
        ],
      },
      {
        name: 'Benefícios',
        icon: Gift,
        module: PlatformModule.BENEFITS,
        children: [
          { name: 'Catálogo', href: '/banking/benefits', icon: Gift },
          { name: 'Minhas Adesões', href: '/banking/my-subscriptions', icon: Heart },
        ],
      },
      { name: 'Meu Perfil', href: '/profile', icon: Users },
      { 
        name: 'Configurações',
        icon: Settings,
        dataTour: 'settings',
        children: [
          { name: 'Conta', href: '/banking/settings', icon: Settings },
          { name: 'Segurança', href: '/banking/settings/security', icon: Shield },
          { name: 'Autenticação 2FA', href: '/banking/security/2fa', icon: Key },
          { name: 'Notificações', href: '/banking/notifications', icon: Bell },
        ],
      },
    ];

    return [...baseItems, ...commonItems];
  };

  // Filter navigation items based on permissions, categories, and modules
  const filterNavItems = (items: NavItem[]): NavItem[] => {
    return items
      .filter(item => {
        // Check admin-only items
        if (item.adminOnly && user?.type !== 'ADMIN') return false;
        // Check super admin only items
        if (item.superAdminOnly && !isSuperAdmin()) return false;
        // Check category-based items (legacy)
        if (item.category && !hasCategory(item.category)) return false;
        // Check module-based items - only filter for non-super-admins
        if (item.module && !isSuperAdmin() && !isModuleEnabled(item.module)) return false;
        // Check granular feature flags (e.g., pixEnabled, tedEnabled)
        if (item.feature && !isSuperAdmin() && !isFeatureEnabled(item.feature.module, item.feature.key)) return false;
        return true;
      })
      .map(item => {
        if (item.children) {
          return {
            ...item,
            children: filterNavItems(item.children),
          };
        }
        return item;
      })
      .filter(item => !item.children || item.children.length > 0);
  };

  // Filter navigation for PENDING_KYC users - only essential items
  const PENDING_KYC_ALLOWED_ROUTES = [
    '/banking/customer',
    '/banking/customer/pix',
    '/banking/customer/statement',
    '/banking/kyc',
    '/banking/kyc/status',
  ];

  const filterPendingKycNav = (items: NavItem[]): NavItem[] => {
    if (user?.status !== 'PENDING_KYC' || !isCorrentista()) return items;
    return items.filter(item => {
      if (item.href) return PENDING_KYC_ALLOWED_ROUTES.includes(item.href);
      if (item.children) {
        const filtered = item.children.filter(
          child => child.href && PENDING_KYC_ALLOWED_ROUTES.includes(child.href)
        );
        if (filtered.length > 0) {
          item.children = filtered;
          return true;
        }
        return false;
      }
      return false;
    });
  };

  const filteredNav = filterPendingKycNav(filterNavItems(getNavigation()));

  // Get panel title based on role
  const getPanelTitle = () => {
    if (isSuperAdmin()) return 'Super Admin';
    if (isCorrentista()) return 'Internet Banking';
    return 'Hub Business';
  };

  const getPanelSubtitle = () => {
    if (isSuperAdmin()) return 'Gestão Global';
    if (isCorrentista()) return 'Sua Conta Digital';
    return 'Financial Platform';
  };

  const renderNavItem = (item: NavItem, depth = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedMenus.includes(item.name);
    const isActive = item.href ? location.pathname === item.href : false;
    const isChildActive = item.children?.some(child => location.pathname === child.href);

    if (hasChildren) {
      return (
        <li key={item.name}>
          <button
            onClick={() => toggleMenu(item.name)}
            data-tour={item.dataTour}
            className={cn(
              'nav-item w-full',
              (isExpanded || isChildActive) && 'text-primary-400',
              sidebarCollapsed && 'justify-center px-3'
            )}
          >
            <item.icon className="w-5 h-5 flex-shrink-0" />
            {!sidebarCollapsed && (
              <>
                <span className="flex-1 text-left flex items-center gap-2">
                  {item.name}
                  {item.showAlertBadge && <AlertBadge showIcon={false} className="ml-1" />}
                </span>
                <ChevronDown
                  className={cn(
                    'w-4 h-4 transition-transform',
                    isExpanded && 'rotate-180'
                  )}
                />
              </>
            )}
            {sidebarCollapsed && item.showAlertBadge && (
              <div className="absolute -top-1 -right-1">
                <AlertBadge showIcon={false} />
              </div>
            )}
          </button>
          <AnimatePresence>
            {isExpanded && !sidebarCollapsed && (
              <motion.ul
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden ml-4 mt-1 space-y-1 border-l border-surface-800 pl-3"
              >
                {item.children?.map(child => renderNavItem(child, depth + 1))}
              </motion.ul>
            )}
          </AnimatePresence>
        </li>
      );
    }

    return (
      <li key={item.name}>
        <NavLink
          to={item.href || '#'}
          data-tour={item.dataTour}
          className={cn(
            'nav-item',
            isActive && 'active',
            sidebarCollapsed && 'justify-center px-3'
          )}
        >
          <item.icon className="w-5 h-5 flex-shrink-0" />
          {!sidebarCollapsed && (
            <span className="flex items-center gap-2">
              {item.name}
              {item.showAlertBadge && <AlertBadge showIcon={false} className="ml-1" />}
            </span>
          )}
          {sidebarCollapsed && item.showAlertBadge && (
            <div className="absolute -top-1 -right-1">
              <AlertBadge showIcon={false} />
            </div>
          )}
        </NavLink>
      </li>
    );
  };

  return (
    <motion.aside
      ref={sidebarRef}
      initial={false}
      animate={{ width: actualWidth }}
      className="fixed left-0 top-0 h-screen bg-surface-950/80 backdrop-blur-xl border-r border-surface-800 z-40 flex flex-col"
    >
      {/* Resize Handle */}
      {!sidebarCollapsed && (
        <div
          onMouseDown={handleMouseDown}
          className={cn(
            "absolute right-0 top-0 bottom-0 w-1 cursor-col-resize group z-50",
            "hover:bg-primary-500/50 transition-colors",
            isResizing && "bg-primary-500"
          )}
        >
          <div className={cn(
            "absolute right-0 top-1/2 -translate-y-1/2 w-4 h-8 -mr-1.5",
            "flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity",
            isResizing && "opacity-100"
          )}>
            <GripVertical className="w-3 h-3 text-primary-400" />
          </div>
        </div>
      )}

      {/* Logo */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-surface-800">
        <motion.div
          initial={false}
          animate={{ opacity: sidebarCollapsed ? 0 : 1 }}
          className="flex items-center gap-3"
        >
          <div className={cn(
            "w-10 h-10 rounded-xl flex items-center justify-center",
            isSuperAdmin() 
              ? "bg-gradient-to-br from-yellow-500 to-orange-500" 
              : isCorrentista()
              ? "bg-gradient-to-br from-green-500 to-emerald-500"
              : "bg-gradient-to-br from-primary-500 to-accent-500"
          )}>
            {isSuperAdmin() ? (
              <Crown className="w-6 h-6 text-white" />
            ) : isCorrentista() ? (
              <Wallet className="w-6 h-6 text-white" />
            ) : (
              <Zap className="w-6 h-6 text-white" />
            )}
          </div>
          {!sidebarCollapsed && (
            <div>
              <h1 className="font-bold text-surface-100">{getPanelTitle()}</h1>
              <p className="text-xs text-surface-500">{getPanelSubtitle()}</p>
            </div>
          )}
        </motion.div>
        <button
          onClick={toggleSidebar}
          className="p-2 rounded-lg text-surface-400 hover:text-surface-100 hover:bg-surface-800 transition-colors"
        >
          <ChevronLeft
            className={cn(
              'w-5 h-5 transition-transform',
              sidebarCollapsed && 'rotate-180'
            )}
          />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-3">
        <ul className="space-y-1">
          {filteredNav.map((item) => renderNavItem(item))}
        </ul>
      </nav>

      {/* Tenant Info with Setup Progress */}
      {!sidebarCollapsed && user?.tenantId && !isSuperAdmin() && (
        <div className="px-4 py-3 border-t border-surface-800">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-xs text-surface-500 min-w-0">
              <Building2 className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{user.tenantName || user.tenantId.slice(0, 12)}...</span>
            </div>
            {/* Setup Progress inline - only for Admins */}
            {user?.type === 'ADMIN' && <SetupProgress />}
          </div>
        </div>
      )}

      {/* User Footer */}
      <div className="p-4 border-t border-surface-800">
        {!sidebarCollapsed && (
          <div className="glass rounded-lg p-3">
            <div className="flex items-center gap-3">
              <div className={cn(
                "w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium",
                isSuperAdmin()
                  ? "bg-gradient-to-br from-yellow-500 to-orange-500"
                  : isCorrentista()
                  ? "bg-gradient-to-br from-green-500 to-emerald-500"
                  : "bg-gradient-to-br from-primary-500 to-accent-500"
              )}>
                {user?.name?.charAt(0) || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-surface-100 truncate">
                  {user?.name || 'Usuário'}
                </p>
                <p className="text-xs text-surface-500 truncate">
                  {isSuperAdmin() ? 'Super Admin' : 
                   user?.type === 'ADMIN' ? 'Administrador' : 
                   user?.type === 'CUSTOMER' ? 'Correntista' :
                   user?.type || 'Usuário'}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </motion.aside>
  );
}
